import { useEffect, useState } from 'react';

/**
 * ¿Coincide `query` con el viewport/las preferencias del navegador? Se lee al montar
 * y se vuelve a evaluar cuando el `MediaQueryList` emite `change` (E07-F01, R9 y R10).
 *
 * Lo usan `SeasonalDecor` y `SeasonalOverlays` (`prefers-reduced-motion`) y `Header`
 * (`max-width: 991.98px`, la misma cadena que el SCSS para que CSS y montaje coincidan).
 * Sin `matchMedia` (jsdom sin mock) responde `false`. No importa nada de `hooks/`.
 */
const mediaQueryList = (query: string): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query)
    : null;

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => mediaQueryList(query)?.matches ?? false);

  useEffect(() => {
    const mql = mediaQueryList(query);
    if (!mql) return undefined;
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent | MediaQueryList) => setMatches(e.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14: sólo la API antigua.
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
};
